import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
  DrawerFooter,
} from '../atoms/drawer';
import { Button } from '../atoms/button';
import { Input } from '../atoms/input';
import { Label } from '../atoms/label';
import { Badge } from '../atoms/badge';
import { Separator } from '../atoms/separator';
import { Textarea } from '../atoms/textarea';
import Icon from '../molecules/Icon';
import RichTextEditor, { RichTextEditorRef } from '../molecules/RichTextEditor';
import { ChevronLeft, CircleCheck, LoaderCircle, X } from 'lucide-react';

// Import shared types - avoid duplication
import type { ProjectFormData } from '../templates/ProjectDetailsTemplate';
// Analytics: project.updated tracked via Redux middleware (old import removed)

export interface ProjectDetailsModalProps {
  isOpen: boolean;
  onDrawerClose: () => void;
  onUpdate: (data: ProjectFormData) => Promise<void>;
  initialValues?: Partial<ProjectFormData>;
  projectId?: string;
  isUpdating?: boolean;
  isWeb?: boolean;
}

/**
 * ProjectDetailsModal - Pure presentation component for editing project context
 *
 * This component provides a drawer interface for updating project details including:
 * - Project name and description
 * - Main user persona
 * - Design process (Double Diamond, Design Sprint, Design Thinking)
 * - User role and Ideoz role
 *
 * Features:
 * - Change detection to enable/disable save button
 * - Rich text editor for project description
 * - Character limits on text fields
 * - Informational banner when context is incomplete
 * - Loading states during updates
 *
 * All text preserved exactly from original implementation.
 */
export const ProjectDetailsModal: React.FC<ProjectDetailsModalProps> = ({
  isOpen,
  onDrawerClose,
  onUpdate,
  initialValues = {},
  projectId,
  isUpdating = false,
  isWeb = false,
}) => {
  const [formData, setFormData] = useState<Partial<ProjectFormData>>({
    name: initialValues.name || '',
    description: initialValues.description || '',
    main_persona: initialValues.main_persona || '',
    design_process: initialValues.design_process || '',
    user_role: initialValues.user_role || '',
    ideoz_role: initialValues.ideoz_role || '',
    industry: initialValues.industry || '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showBanner, setShowBanner] = useState(true);
  const editorRef = useRef<RichTextEditorRef>(null);
  const originalDataRef = useRef<Partial<ProjectFormData> | null>(null);

  // Normalization utility to compare field values
  function normalize(val?: string) {
    if (!val) return '';
    return val
      .replace(/<p><br\/?><\/p>/gi, '') // empty paragraph produced by editors
      .replace(/&nbsp;/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  // Return only the keys of fields that changed
  function getChangedFields(
    original: Partial<ProjectFormData> | null,
    current: Partial<ProjectFormData>
  ): string[] {
    if (!original) return [];
    const changed: string[] = [];
    const keys = new Set([...Object.keys(original), ...Object.keys(current)]) as Set<
      keyof ProjectFormData
    >;
    keys.forEach((k) => {
      const before = original[k];
      const after = current[k];
      if (normalize(before as string | undefined) !== normalize(after as string | undefined)) {
        changed.push(String(k));
      }
    });
    return changed;
  }

  // Update form data when initialValues change (for when project switches)
  useEffect(() => {
    if (isLoading) return;
    const newData: Partial<ProjectFormData> = {
      name: initialValues.name || '',
      description: initialValues.description || '',
      main_persona: initialValues.main_persona || '',
      design_process: initialValues.design_process || '',
      user_role: initialValues.user_role || '',
      ideoz_role: initialValues.ideoz_role || '',
      industry: initialValues.industry || '',
    };
    setFormData(newData);
    // Update baseline snapshot when initial values change
    originalDataRef.current = newData;
  }, [initialValues, isLoading]);

  const designProcessOptions = [
    { label: 'Double Diamond', value: 'double_diamond' },
    { label: 'Design Sprint', value: 'design_sprint' },
    { label: 'Design Thinking', value: 'design_thinking' },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.name?.trim() && !isLoading && !isUpdating) {
      const changedFields = getChangedFields(originalDataRef.current, formData);

      setIsLoading(true);
      try {
        const htmlContent = editorRef.current?.getContent() || '';
        const updatedData: ProjectFormData = {
          name: formData.name || '',
          description: htmlContent,
          main_persona: formData.main_persona || '',
          design_process: formData.design_process || '',
          ideoz_role: formData.ideoz_role || '',
          user_role: formData.user_role || '',
          industry: formData.industry || '',
        };
        await onUpdate(updatedData);
        originalDataRef.current = formData;
        // Analytics: project.updated tracked via Redux middleware
        onDrawerClose();
      } catch (error) {
        console.error('Failed to update project:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleInputChange = (field: keyof ProjectFormData, value: string) => {
    setFormData((prev) => {
      let nextValue = value;
      // Toggle off if clicking the same design_process value
      if (field === 'design_process' && prev.design_process === value) {
        nextValue = '';
      }
      return {
        ...prev,
        [field]: nextValue,
        // Always preserve the current description from the editor unless editing it
        description:
          field !== 'description' ? editorRef.current?.getContent() || prev.description : nextValue,
      };
    });
  };

  const handleDescriptionChange = (content: string) => {
    // Update the description in form data when RichTextEditor content changes
    setFormData((prev) => ({ ...prev, description: content }));
  };

  // Initialize form when modal opens
  useEffect(() => {
    if (isOpen) {
      const initializedData: Partial<ProjectFormData> = {
        name: initialValues.name || '',
        description: initialValues.description || '',
        main_persona: initialValues.main_persona || '',
        design_process: initialValues.design_process || 'double_diamond',
        user_role: initialValues.user_role || '',
        ideoz_role: initialValues.ideoz_role || '',
        industry: initialValues.industry || '',
      };
      setFormData(initializedData);
      originalDataRef.current = initializedData;
      editorRef.current?.setContent(initialValues.description || '');
    }
  }, [isOpen, initialValues]);

  // Determine if any field has changed
  const hasChanges = useMemo(() => {
    if (!originalDataRef.current) return false;
    const keys: (keyof ProjectFormData)[] = [
      'name',
      'description',
      'main_persona',
      'design_process',
      'user_role',
      'ideoz_role',
      'industry',
    ];
    return keys.some((k) => normalize(formData[k]) !== normalize(originalDataRef.current?.[k]));
  }, [formData]);

  return (
    <Drawer open={isOpen} onOpenChange={onDrawerClose} shouldScaleBackground={false} handleOnly>
      <DrawerContent className={`mx-auto ${isWeb ? 'max-w-[520px]' : 'max-w-[480px]'}`}>
        <DrawerHeader className="px-5 py-5 border-gray-200">
          <div className="flex items-center justify-between full-width flex-1">
            <DrawerTitle className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <DrawerClose asChild>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={onDrawerClose}
                    variant="ghost"
                    size="icon-default"
                    className="h-8 w-8 !gap-1 hover:bg-gray-100"
                  >
                    <Icon icon={ChevronLeft} size={isWeb ? 22 : 18} strokeWidth={2} />
                  </Button>
                  <h2
                    className="text-lg font-medium text-gray-800"
                    data-testid="project-details-modal-title"
                  >
                    Update project context
                  </h2>
                </div>
              </DrawerClose>
            </DrawerTitle>
          </div>
        </DrawerHeader>

        <div className="px-4 pb-4 flex-1 overflow-y-auto">
          {/* Banner content */}
          {showBanner &&
          (!formData.main_persona ||
            !formData.ideoz_role ||
            !formData.design_process ||
            !formData.description ||
            !formData.user_role) ? (
            <div
              className="flex items-center justify-between mb-5 py-2 px-4 bg-success-50 border rounded-xl border-success-200"
              data-testid="project-context-banner"
            >
              <span className="text-gray-700 text-base">
                The more context you share, the smarter Ideoz becomes
              </span>
              <Button
                onClick={() => setShowBanner(false)}
                variant="ghost"
                size="sm"
                className="p-0 m-0 h-5 hover:bg-gray-100"
                data-testid="btn-close-banner"
              >
                <Icon icon={X} size={18} strokeWidth={2.5} />
              </Button>
            </div>
          ) : null}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Project Information Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold" style={{ color: '#4B579F' }}>
                Project Information
              </h3>

              {/* Project Name Field */}
              <div className="space-y-2">
                <Label htmlFor="project-name">Project name</Label>
                <Input
                  id="project-name"
                  value={formData.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('name', e.target.value)
                  }
                  placeholder="Onboarding new students"
                  data-testid="input-project-name"
                />
              </div>

              {/* Project Description Field */}
              <div className="space-y-2">
                <Label>Explain the project scope, what to focus on, and what not to.</Label>

                <div className="min-h-[120px]">
                  <RichTextEditor
                    ref={editorRef}
                    initialValue={formData.description}
                    placeholder="project scope details"
                    onChange={handleDescriptionChange}
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Framing Context Section */}
            <div className="my-7">
              <h3 className="text-sm font-semibold" style={{ color: '#4B579F' }}>
                Framing Context
              </h3>

              {/* Main User Field */}
              <div className="mt-6">
                <Label className="mb-3 block" htmlFor="main-user">
                  Who is your main user?
                </Label>
                <Input
                  id="main-user"
                  value={formData.main_persona}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('main_persona', e.target.value.slice(0, 255))
                  }
                  placeholder="E.g. Customers who are frustrating in checkout experience"
                  maxLength={255}
                  data-testid="input-main-persona"
                />
                <div className="text-xs text-gray-500 text-right mt-1">
                  {formData.main_persona?.length || 0}/255
                </div>
              </div>

              {/* Industry Field */}
              <div className="mt-6">
                <Label className="mb-3 block" htmlFor="industry">
                  What industry are you working in?
                </Label>
                <Input
                  id="industry"
                  value={formData.industry}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('industry', e.target.value.slice(0, 255))
                  }
                  placeholder="E.g. E-commerce, Healthcare, Fintech"
                  maxLength={255}
                  data-testid="input-industry"
                />
                <div className="text-xs text-gray-500 text-right mt-1">
                  {formData.industry?.length || 0}/255
                </div>
              </div>
              {/* Design Process Field - dev only */}
              {(typeof import.meta !== 'undefined' &&
                (import.meta as any).env?.VITE_NODE_ENV === 'development') && (
              <div className="mt-6">
                <Label className="mb-3 block">
                  What is your design process used in this project?
                </Label>
                <div className="flex gap-2 flex-wrap">
                  {designProcessOptions.map((option) => (
                    <Badge
                      className="cursor-pointer"
                      size="xs"
                      key={option.value}
                      variant={formData.design_process === option.value ? 'success' : 'secondary'}
                      onClick={() => handleInputChange('design_process', option.value as any)}
                      data-testid={`badge-design-process-${option.value}`}
                      leftIcon={
                        formData.design_process === option.value && (
                          <Icon icon={CircleCheck} size={14} className="stroke-current" />
                        )
                      }
                    >
                      {option.label}
                    </Badge>
                  ))}
                </div>
              </div>
              )} 

              {/* User Role Field */}
              <div className="mt-6">
                <Label className="mb-3 block" htmlFor="user-role">
                  Your Role in This Project
                </Label>
                <Textarea
                  id="user-role"
                  value={formData.user_role}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    handleInputChange('user_role', e.target.value.slice(0, 255))
                  }
                  placeholder="E.g. Freelance UX designer collaborating with PMs"
                  maxLength={255}
                  data-testid="textarea-user-role"
                />
                <div className="text-xs text-gray-500 text-right mt-1">
                  {formData.user_role?.length || 0}/255
                </div>
              </div>

              {/* Ideoz Role Field */}
              <div className="mt-6">
                <Label className="mb-3 block" htmlFor="ideoz-role">
                  How should Ideoz act?
                </Label>
                <Textarea
                  id="ideoz-role"
                  value={formData.ideoz_role}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    handleInputChange('ideoz_role', e.target.value.slice(0, 255))
                  }
                  placeholder="Example: Expert product & UX designer using Double Diamond, focused on framing checkout problems in Saudi Arabia"
                  maxLength={255}
                  data-testid="textarea-ideoz-role"
                />
                <div className="text-xs text-gray-500 text-right mt-1">
                  {formData.ideoz_role?.length || 0}/255
                </div>
              </div>
            </div>
          </form>
        </div>

        <DrawerFooter className="px-4 py-4 border-t border-gray-200">
          <div className="flex items-center justify-end gap-3">
            <Button
              variant="secondary-gray"
              size={isWeb ? 'sm' : 'xs'}
              onClick={onDrawerClose}
              data-testid="btn-cancel-project-details"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size={isWeb ? 'sm' : 'xs'}
              type="submit"
              onClick={handleSubmit}
              disabled={isLoading || isUpdating || !formData.name?.trim() || !hasChanges}
              data-testid="btn-update-project-details"
            >
              {(isLoading || isUpdating) && (
                <Icon
                  icon={LoaderCircle}
                  size={18}
                  strokeWidth={2}
                  color="#fff"
                  className="animate-spin mr-2"
                />
              )}
              {isLoading || isUpdating ? 'Updating...' : 'Update'}
            </Button>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
